import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getAllBookingForAnalytics } from "../service/api";
import LineChart from "./AnalyticsCharts/LineChart";
import BarChart from "./AnalyticsCharts/BarChart";
import MultiBarChart from "./AnalyticsCharts/MultiBarChart";
import PieChart from "./AnalyticsCharts/PieChart";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const Analytics = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const state = (location.state as { id?: number | string; name?: string } | null) ?? null;
    const id = state?.id ?? searchParams.get("partner_id");
    const name = state?.name ?? "Partner";
    const [bookings, setBookings] = useState([]);

    useEffect(() => {
        if (id) {
            getBooking(id);
        } else {
            setBookings([]);
        }
    }, [id]);

    const getBooking = async (partnerId: number | string) => {
        try {
            const res = await getAllBookingForAnalytics(partnerId);
            setBookings(res.data.data);
        } catch {
            setBookings([]);
        }
    };

    const calculateAnalytics = (bookings: any) => {
        const totalBookings = bookings.length;
        const totalRevenue = bookings.reduce((sum: number, booking: { slots: { price: number | string }[] }) => {
            const slotRevenue = booking.slots.reduce((slotSum: number, slot: { price: number | string }) => {
                return slotSum + parseFloat(slot?.price as string);
            }, 0);
            return sum + slotRevenue;
        }, 0);
        const commission = totalRevenue * 0.10;
        const commissionTotal = totalRevenue - commission;

        const hoursCount: Record<string, number> = {};
        bookings.forEach((booking: { slots: { from_time: string, to_time: string }[] }) => {
            booking.slots.forEach((slot: { from_time: string, to_time: string }) => {
                const timeRange = `${slot?.from_time} - ${slot?.to_time}`;
                hoursCount[timeRange] = (hoursCount[timeRange] || 0) + 1;
            });
        });
        const mostSelectedHour = Object.keys(hoursCount).reduce(
            (a, b) => (hoursCount[a] > hoursCount[b] ? a : b),
            Object.keys(hoursCount)[0] || 'No data'
        );

        const revenueByBoxType = bookings.reduce((acc: any, booking: { box_type: any, slots: { price: any }[] }) => {
            const boxType = booking.box_type;
            booking.slots.forEach((slot: { price: any }) => {
                acc[boxType] = (acc[boxType] || 0) + parseFloat(slot?.price);
            });
            return acc;
        }, {});

        const bookingsByDate = bookings.reduce((acc: any, booking: { created_at: any }) => {
            const date = new Date(booking.created_at).toISOString().split('T')[0];
            acc[date] = (acc[date] || 0) + 1;
            return acc;
        }, {});

        return { totalBookings, totalRevenue, bookingsByDate, mostSelectedHour, revenueByBoxType, commission, commissionTotal };
    };

    const { totalBookings, totalRevenue, bookingsByDate, mostSelectedHour, revenueByBoxType, commission, commissionTotal } = calculateAnalytics(bookings);

    if (!id) {
        return (
            <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Analytics needs a partner</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-muted-foreground">
                            Open analytics from the partners list, or pass <code className="text-sm bg-muted px-1 rounded">partner_id</code> in the URL.
                        </p>
                        <Button onClick={() => navigate("/partners")}>Go to Partners</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold text-foreground">Analytics</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    Booking and revenue for <span className="font-medium text-foreground">{name}</span>.
                </p>
            </div>

            {/* Top stat cards */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
                <div className="bg-[#34495E] hover:bg-[#273442] text-white text-center shadow-sm rounded-xl border border-white/10 cursor-pointer p-6 transition-colors">
                    <p className="text-3xl lg:text-5xl font-bold">{totalBookings}</p>
                    <p className="text-sm lg:text-xl opacity-90 mt-2">Total bookings</p>
                </div>
                <div className="bg-[#16A085] hover:bg-[#127863] text-white text-center shadow-sm rounded-xl border border-white/10 cursor-pointer p-6 transition-colors">
                    <p className="text-2xl lg:text-4xl font-bold break-words">{mostSelectedHour}</p>
                    <p className="text-sm lg:text-xl opacity-90 mt-2">Most booked time window</p>
                </div>
            </section>

            {/* Line chart */}
            <Card className="overflow-hidden">
                <CardContent className="p-4 sm:p-6">
                    <LineChart bookings={bookings} />
                </CardContent>
            </Card>

            {/* Revenue heading */}
            <h2 className="text-lg font-semibold text-foreground pt-2">Revenue</h2>

            {/* Revenue stat cards */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
                <div className="bg-[#34495E] min-h-[150px] hover:bg-[#273442] text-white text-center shadow-sm rounded-xl border border-white/10 cursor-pointer p-6 transition-colors flex flex-col items-center justify-center">
                    <p className="text-3xl lg:text-5xl font-bold">{totalRevenue}</p>
                    <p className="text-sm lg:text-xl opacity-90 mt-2">Total revenue</p>
                </div>
                <div className="bg-[#16A085] min-h-[150px] hover:bg-[#127863] text-white text-left shadow-sm rounded-xl border border-white/10 cursor-pointer p-6 transition-colors">
                    <p className="text-lg lg:text-xl font-bold leading-snug">
                        Commission: {commission}
                        <br />
                        Net after commission: {commissionTotal}
                    </p>
                    <p className="text-sm lg:text-base opacity-90 text-center mt-2">Commission at 10%</p>
                </div>
            </section>

            {/* Charts */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
                <Card className="overflow-hidden">
                    <CardContent className="p-4 sm:p-6">
                        <BarChart bookings={bookings} />
                    </CardContent>
                </Card>
                <Card className="overflow-hidden">
                    <CardContent className="p-4 sm:p-6">
                        <MultiBarChart bookings={bookings} />
                    </CardContent>
                </Card>
            </section>

            <Card className="overflow-hidden">
                <CardContent className="p-4 sm:p-6">
                    <PieChart bookings={bookings} />
                </CardContent>
            </Card>
        </div>
    );
};

export default Analytics;
